import React, { useEffect, useState } from 'react';
import { Loader2, ArrowLeft, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { atlasApi, AtlasUser, Pesquisa, RespostaPesquisaItem } from '../shared/atlasApi';
import FeedbackBanner, { Feedback } from '../shared/FeedbackBanner';

interface Props {
  user: AtlasUser;
  pesquisaId: string;
  onVoltar: () => void;
  onRespondida?: () => void;
}

const Responder: React.FC<Props> = ({ user, pesquisaId, onVoltar, onRespondida }) => {
  const [pesquisa, setPesquisa] = useState<Pesquisa | null>(null);
  const [motivo, setMotivo] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [respostas, setRespostas] = useState<Record<string, number | string>>({});
  const [enviando, setEnviando] = useState(false);
  const [fb, setFb] = useState<Feedback>(null);
  const [enviada, setEnviada] = useState(false);

  useEffect(() => {
    setLoading(true);
    atlasApi.getMinhaPesquisa(user, pesquisaId)
      .then(r => {
        if (r.pendente && r.pesquisa) setPesquisa(r.pesquisa);
        else setMotivo(r.motivo || 'pesquisa indisponível');
      })
      .catch(e => setMotivo(e.message || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [user, pesquisaId]);

  const setVal = (pid: string | number, valor: number | string) =>
    setRespostas(p => ({ ...p, [String(pid)]: valor }));

  const enviar = async () => {
    if (!pesquisa) return;
    const payload: RespostaPesquisaItem[] = (pesquisa.perguntas || [])
      .filter(p => respostas[String(p.id)] !== undefined && respostas[String(p.id)] !== '')
      .map(p => ({ pergunta_id: p.id, valor: respostas[String(p.id)] }));
    if (payload.length === 0) { setFb({ tipo: 'erro', msg: 'Responda ao menos uma pergunta.' }); return; }
    setEnviando(true);
    setFb(null);
    try {
      await atlasApi.responderPesquisa(user, pesquisaId, payload);
      setEnviada(true);
      onRespondida?.();
    } catch (e: any) {
      setFb({ tipo: 'erro', msg: e.message || 'Erro ao enviar.' });
    } finally {
      setEnviando(false);
    }
  };

  if (loading) return <div className="p-12 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...</div>;

  if (enviada) {
    return (
      <div className="p-10 text-center bg-white rounded-2xl border border-gray-200 max-w-lg mx-auto">
        <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
        <h3 className="font-bold text-gray-800">Resposta enviada!</h3>
        <p className="text-sm text-gray-500 mt-1">Obrigado por participar.</p>
        <button onClick={onVoltar} className="mt-4 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium">Voltar</button>
      </div>
    );
  }

  if (!pesquisa) {
    return (
      <div className="space-y-4 max-w-lg">
        <button onClick={onVoltar} className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Voltar</button>
        <div className="p-10 text-center bg-white rounded-2xl border border-gray-200 text-gray-500">{motivo}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <button onClick={onVoltar} className="text-sm text-gray-500 hover:text-gray-800 flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Voltar</button>

      <div>
        <h2 className="text-lg font-bold text-gray-900">{pesquisa.nome}</h2>
        {pesquisa.descricao && <p className="text-sm text-gray-500 mt-0.5">{pesquisa.descricao}</p>}
        {pesquisa.anonima && (
          <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1"><ShieldCheck className="w-3.5 h-3.5" /> Respostas anônimas — sua identidade não é registrada.</p>
        )}
      </div>

      <div className="space-y-4">
        {(pesquisa.perguntas || []).map(p => (
          <div key={String(p.id)} className="bg-white rounded-2xl border border-gray-200 p-5">
            <p className="font-medium text-gray-800 mb-3">{p.texto}</p>

            {p.tipo === 'nps' && (
              <div className="flex flex-wrap gap-1.5">
                {Array.from({ length: 11 }, (_, i) => i).map(n => (
                  <button key={n} onClick={() => setVal(p.id, n)}
                    className={`w-9 h-9 rounded-lg text-sm font-semibold border transition ${respostas[String(p.id)] === n ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300'}`}>
                    {n}
                  </button>
                ))}
                <div className="w-full flex justify-between text-[10px] text-gray-400 mt-1 px-0.5">
                  <span>Nada provável</span><span>Muito provável</span>
                </div>
              </div>
            )}

            {p.tipo === 'escala_5' && (
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} onClick={() => setVal(p.id, n)}
                    className={`flex-1 h-11 rounded-lg text-sm font-semibold border transition ${respostas[String(p.id)] === n ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300'}`}>
                    {n}
                  </button>
                ))}
              </div>
            )}

            {p.tipo === 'sim_nao' && (
              <div className="flex gap-2">
                {['sim', 'nao'].map(v => (
                  <button key={v} onClick={() => setVal(p.id, v)}
                    className={`flex-1 h-11 rounded-lg text-sm font-semibold border transition capitalize ${respostas[String(p.id)] === v ? 'bg-purple-600 text-white border-purple-600' : 'bg-white text-gray-600 border-gray-200 hover:border-purple-300'}`}>
                    {v === 'nao' ? 'Não' : 'Sim'}
                  </button>
                ))}
              </div>
            )}

            {p.tipo === 'texto' && (
              <textarea
                value={(respostas[String(p.id)] as string) || ''}
                onChange={e => setVal(p.id, e.target.value)}
                placeholder="Escreva aqui..."
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 min-h-[80px] resize-y focus:outline-none focus:ring-2 focus:ring-purple-200"
              />
            )}
          </div>
        ))}
      </div>

      <FeedbackBanner feedback={fb} />

      <div className="flex justify-end">
        <button onClick={enviar} disabled={enviando} className="px-5 py-2.5 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium flex items-center gap-2 disabled:opacity-60">
          {enviando && <Loader2 className="w-4 h-4 animate-spin" />} Enviar respostas
        </button>
      </div>
    </div>
  );
};

export default Responder;
